// @ts-nocheck
import { type UseQueryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import VaultClient from "../clients/VaultClient";
import { vaultKeys } from "./queryKeys";
import { useApi } from "./useApi";

/**
 * onMutateVault will update the cache within react-query for vault records,
 * this will prevent the need to constantly call refetchVault on components
 * when values are already available in memory from an update.
 *
 * @param client
 * @param updatePayload
 * @param investmentId
 */
export const onMutateVault = async (client: any, updatePayload: object, investmentId: string) => {
  await client.cancelQueries(vaultKeys.detail(investmentId));
  const previousVault: object | undefined = client.getQueryData(vaultKeys.detail(investmentId));
  const newVault = {
    ...previousVault,
    ...updatePayload,
  };
  client.setQueryData(vaultKeys.detail(investmentId), newVault);

  return { previousVault, newVault };
};

/**
 *
 * @param client
 * @param investmentId
 * @param error
 * @param newVault
 * @param context
 */
export const onErrorVault = async (client: any, investmentId: string, error: any, newVault: object, context: any) => {
  client.setQueryData(vaultKeys.detail(investmentId), context.previousVault);
};

export const useGetVault = (vaultId?: string | null, options: UseQueryOptions = {}) => {
  const vaultApi = useApi(VaultClient);

  return useQuery({
    queryKey: vaultKeys.detail(vaultId as string),
    queryFn: () => vaultApi.getVault(vaultId as string),
    enabled: !!vaultId,
    ...options,
  });
};

export const useGetUserVaults = (userId: string, status?: string, offeringId?: string, options = {}) => {
  const vaultApi = useApi(VaultClient);

  return useQuery(
    ["user-vaults", userId, status, offeringId],
    () => vaultApi.getUserVaults(userId, status, offeringId),
    {
      enabled: !!userId,
      ...options,
    },
  );
};

export const useSaveVaultProfile = (investmentId: string, options = {}) => {
  const client = useQueryClient();
  const vaultApi = useApi(VaultClient);

  return useMutation((data: Record<string, any>) => vaultApi.saveProfile(investmentId, data), {
    onMutate: async (payload) => await onMutateVault(client, payload, investmentId),
    onError: async (err, newData, context) => await onErrorVault(client, investmentId, err, newData, context),
    onSuccess: async () => {
      // Account-type changes remove additional signers server-side. Refetch
      // inactive queries too — the app disables refetchOnMount, so a stale
      // cache would keep showing removed signers on the signers/review steps.
      await client.invalidateQueries({ queryKey: ["investment", investmentId, "vaults"], refetchType: "all" });
      await client.invalidateQueries({ queryKey: ["investment", investmentId, "signatures"], refetchType: "all" });
    },
    ...options,
  });
};

export const useSaveVaultAddress = (investmentId: string, options = {}) => {
  const client = useQueryClient();
  const vaultApi = useApi(VaultClient);

  return useMutation((data: Record<string, any>) => vaultApi.saveAddress(investmentId, data), {
    onMutate: async (payload) => await onMutateVault(client, payload, investmentId),
    onError: async (err, newData, context) => await onErrorVault(client, investmentId, err, newData, context),
    ...options,
  });
};

export const useSaveVaultEntity = (investmentId: string, options = {}) => {
  const client = useQueryClient();
  const vaultApi = useApi(VaultClient);

  return useMutation((data: Record<string, any>) => vaultApi.saveEntity(investmentId, data), {
    onMutate: async (payload) => await onMutateVault(client, payload, investmentId),
    onError: async (err, newData, context) => await onErrorVault(client, investmentId, err, newData, context),
    ...options,
  });
};

export const useSaveVaultMetadata = (investmentId: string, options = {}) => {
  const client = useQueryClient();
  const vaultApi = useApi(VaultClient);

  return useMutation((data: Record<string, any>) => vaultApi.saveMetadata(investmentId, data), {
    onMutate: async (payload) => await onMutateVault(client, payload, investmentId),
    onError: async (err, newData, context) => await onErrorVault(client, investmentId, err, newData, context),
    ...options,
  });
};

export const useSaveVaultAdditionalInfo = (investmentId: string, options = {}) => {
  const client = useQueryClient();
  const vaultApi = useApi(VaultClient);

  return useMutation((data: Record<string, any>) => vaultApi.saveAdditionalInfo(investmentId, data), {
    onMutate: async (payload) => await onMutateVault(client, payload, investmentId),
    onError: async (err, newData, context) => await onErrorVault(client, investmentId, err, newData, context),
    ...options,
  });
};

export const useDisconnectVaultRecord = (investmentId: string) => {
  const vaultApi = useApi(VaultClient);

  return useMutation((vaultRecordId: string) => vaultApi.disconnectVaultRecord(investmentId, vaultRecordId));
};

export const useGetInvestmentVaults = (investmentId: string, options = {}) => {
  const vaultApi = useApi(VaultClient);
  return useQuery(["investment", investmentId, "vaults"], () => vaultApi.getInvestmentVault(investmentId), {
    enabled: !!investmentId,
    ...options,
  });
};

export const useGetFullVaultRecords = (investmentId: string, options = {}) => {
  const vaultApi = useApi(VaultClient);
  return useQuery(["investment", investmentId, "vaults", "full"], () => vaultApi.getFullVaultRecords(investmentId), {
    enabled: !!investmentId,
    ...options,
  });
};

export const useUpdateVault = (vaultId: string, investmentId: string, options = {}) => {
  const client = useQueryClient();
  const vaultApi = useApi(VaultClient);

  return useMutation((data: Record<string, any>) => vaultApi.updateVault(vaultId, investmentId, data), {
    onMutate: async (payload) => await onMutateVault(client, payload, vaultId),
    onError: async (err, newData, context) => await onErrorVault(client, vaultId, err, newData, context),
    onSuccess: async () => {
      // Same as useSaveVaultProfile: account-type edits can remove signers
      await client.invalidateQueries({ queryKey: ["investment", investmentId, "vaults"], refetchType: "all" });
      await client.invalidateQueries({ queryKey: ["investment", investmentId, "signatures"], refetchType: "all" });
    },
    ...options,
  });
};
